import { ReactNode, SyntheticEvent } from 'react';

import {
  AppIcon,
  AppIconProps,
  ICON_SIZES,
} from '@backrs_ui/common-ui/src/designSystem/icons/AppIcon';

import { Box, Chip, IconButton, SxProps, Tab, Tabs, Theme, Typography } from '@mui/material';

type HorizontalMenuTabStyles = Readonly<{
  container: SxProps<Theme>;
  tab: SxProps<Theme>;
  tabs: SxProps<Theme>;
  tabIndicator?: SxProps<Theme>;
  scroll?: boolean;
  numbered?: boolean;
}>;

const getHorizontalMenuTabStyles = (
  variant: HorizontalMenuTabsProps['variant'],
  numbered?: boolean,
): HorizontalMenuTabStyles => {
  switch (variant) {
    case 'box':
      return {
        container: boxContainerSx,
        tab: boxTabSx,
        tabs: boxTabsSx,
        scroll: false,
      };
    case 'minimal':
      return {
        container: minimalContainerSx,
        tab: minimalTabSx,
        tabs: minimalTabsSx,
        scroll: false,
      };
    case 'minimal-highlighted':
      return {
        container: minimalHighlightedContainerSx,
        tab: minimalHighlightedTabSx(numbered),
        tabs: minimalHighlightedTabsSx,
        scroll: true,
      };
    case 'filter':
      return {
        container: filterContainerSx,
        tab: filterTabSx,
        tabs: filterTabsSx,
        tabIndicator: filterTabIndicatorSx,
        scroll: false,
      };
    case 'simple':
      return {
        container: simpleContainerSx,
        tab: simpleTabSx,
        tabs: simpleTabsSx,
        tabIndicator: simpleTabIndicatorSx,
        scroll: false,
      };
  }
};

export type HorizontalMenuTabsProps = Readonly<{
  setValue: (arg0: number) => void;
  tabs: string[];
  value: number;
  chipConfig?: {
    label: string;
    iconName?: AppIconProps['iconName'];
    iconColor?: AppIconProps['color'];
    onClick?: () => void;
    chipSxOverride?: SxProps<Theme>;
    strokeWidth?: AppIconProps['strokeWidth'];
  };
  variant: 'minimal' | 'box' | 'filter' | 'minimal-highlighted' | 'simple';
  disabledTabs?: string[];
  numbered?: boolean;
  containerSxOverride?: SxProps<Theme>;
  tabIndicators?: readonly (ReactNode | null)[];
  onCloseTab?: (tabIndex: number) => void;
  closableTabs?: readonly boolean[];
  id?: string;
}>;

export function HorizontalMenuTabs({
  setValue,
  tabs,
  value,
  chipConfig,
  variant,
  numbered,
  disabledTabs,
  containerSxOverride,
  tabIndicators,
  onCloseTab,
  closableTabs,
  id,
}: HorizontalMenuTabsProps) {
  const styles = getHorizontalMenuTabStyles(variant, numbered);

  const handleChange = (e: SyntheticEvent, newValue: number) => {
    e.preventDefault();
    setValue(newValue);
  };

  return (
    <Box id={id} sx={[styles.container, containerSxOverride].filter(Boolean) as SxProps<Theme>}>
      <Tabs
        value={value === -1 ? 0 : value}
        onChange={handleChange}
        sx={styles.tabs}
        TabIndicatorProps={{
          sx: styles.tabIndicator,
        }}
        variant={styles.scroll ? 'scrollable' : 'standard'}
        scrollButtons={false}
        allowScrollButtonsMobile={styles.scroll}
      >
        {tabs.map((t, index) => {
          const indicator = tabIndicators?.[index];
          const tabSlug = t.toLowerCase().replace(/\s+/g, '-');
          const isClosable = closableTabs?.[index] && onCloseTab;

          const closeButton = isClosable ? (
            <IconButton
              id={`${id ? `${id}-` : ''}tab-close-${tabSlug}`}
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onCloseTab(index);
              }}
              sx={tabCloseButtonSx}
            >
              <AppIcon iconName="XClose" diameter="12px" color="Gray/700" strokeWidth="regular" />
            </IconButton>
          ) : null;

          const baseLabel = numbered ? (
            <Box sx={numberedTabLabelSx}>
              <Box sx={numberedTabCircleSx}>
                <Typography variant="text-xs-medium">{index + 1}</Typography>
              </Box>
              {t}
            </Box>
          ) : indicator ? (
            <Box sx={tabWithIndicatorSx}>
              {indicator}
              {t}
            </Box>
          ) : (
            t
          );

          const label = closeButton ? (
            <Box sx={tabWithIndicatorSx}>
              {baseLabel}
              {closeButton}
            </Box>
          ) : (
            baseLabel
          );

          return (
            <Tab
              key={`${t}-${index}`}
              id={`${id ? `${id}-` : ''}tab-${tabSlug}`}
              label={label}
              sx={
                [styles.tab, tabTruncateSx, isClosable && closableTabSx].filter(
                  Boolean,
                ) as SxProps<Theme>
              }
              disableRipple
              disabled={disabledTabs?.includes(t)}
            />
          );
        })}
      </Tabs>

      {chipConfig && (
        <Chip
          id={id ? `${id}-scroll-chip` : 'scroll-chip'}
          variant="pill"
          size="lg"
          type="leading"
          label={chipConfig.label}
          icon={
            chipConfig.iconName && (
              <AppIcon
                iconName={chipConfig.iconName}
                diameter="12px"
                color={chipConfig.iconColor || 'Warning/500'}
                strokeWidth={chipConfig.strokeWidth || 'regular'}
              />
            )
          }
          sx={[chipSx, chipConfig.chipSxOverride].filter(Boolean) as SxProps<Theme>}
          onClick={chipConfig.onClick}
        />
      )}
    </Box>
  );
}

const minimalTabSx: SxProps<Theme> = ({ typography, backrs }) => ({
  ...typography['text-md-semibold'],
  textTransform: 'none',
  color: backrs.colors['Colors/Foreground/fg-brand-secondary'],
  '&.Mui-selected': {
    color: backrs.colors['Colors/Foreground/fg-brand-secondary'],
  },
  '&:hover': {
    color: backrs.colors['Colors/Foreground/fg-brand-secondary'],
  },
});

const minimalHighlightedTabSx =
  (numbered?: boolean): SxProps<Theme> =>
  ({ backrs, typography }: Theme) => ({
    ...typography['text-md-semibold'],
    textTransform: 'none',
    color: backrs.colors['Gray/500'],
    pt: 0,
    pb: numbered ? backrs.spacing['spacing-xs'] : backrs.spacing['spacing-xl'],
    minHeight: numbered ? '36px' : '30px',
    height: numbered ? '36px' : '30px',
    '&.Mui-selected': {
      color: backrs.colors['Brand/700'],
    },
  });

const boxTabSx: SxProps<Theme> = ({ backrs, typography }) => ({
  border: `1px solid ${backrs.colors['Gray/300']}`,
  '&:first-of-type': {
    borderRadius: '8px 0px 0px 8px',
  },
  '&:last-of-type': {
    borderRadius: '0px 8px 8px 0px',
  },
  ...typography['text-sm-semibold'],
  color: backrs.colors['Gray/700'],
  transition: 'all 0.3s ease-in-out',
  backgroundColor: backrs.colors['Colors/Background/bg-primary'],
  '&.Mui-selected': {
    backgroundColor: `${backrs.colors['Colors/Background/bg-active']}`,
    color: backrs.colors['Gray/700'],
  },
  '&:hover': {
    backgroundColor: `${backrs.colors['Gray/50']}`,
    color: backrs.colors['Gray/700'],
  },
  textTransform: 'none',
});

const minimalContainerSx: SxProps<Theme> = ({ backrs }) => ({
  containerType: 'inline-size',
  borderBottom: `1px solid ${backrs.colors['Colors/Foreground/fg-quaternary']}`,
  alignItems: 'flex-start',
  flexWrap: 'wrap-reverse',
  pt: {
    sm: backrs.spacing['spacing-2xl'],
  },
  display: {
    xs: 'none',
    md: 'flex',
  },
  '@container (max-width: 900px)': {
    display: 'none',
  },
  justifyContent: 'space-between',
  maxWidth: `calc(936px - ${backrs.spacing['spacing-7xl']}) !important`,
  justifyItems: 'center',
  width: '-webkit-fill-available',
  alignSelf: 'center',
  position: 'sticky',
  top: 0,
  backgroundColor: backrs.colors['Beige/25'],
  zIndex: 10,
  mx: {
    xs: backrs.spacing['spacing-4xl'],
  },
  boxSizing: 'border-box',
});

const minimalHighlightedContainerSx: SxProps<Theme> = ({ backrs }) => ({
  borderBottom: `1px solid ${backrs.colors['Colors/Foreground/fg-quaternary']}`,
  overflowX: 'auto',
  overflowY: 'hidden',
  scrollBehavior: 'smooth',
  '&::-webkit-scrollbar': {
    height: '4px',
  },
  '&::-webkit-scrollbar-track': {
    backgroundColor: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: backrs.colors['Gray/300'],
    borderRadius: '2px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    backgroundColor: backrs.colors['Gray/400'],
  },
});

const boxContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  borderRadius: backrs.radius['radius-md'],
  border: `1px solid ${backrs.colors['Gray/300']}`,
  boxShadow: backrs.shadows['shadow-xs-skeuomorphic'],
  pt: {
    sm: backrs.spacing['spacing-none'],
  },
  position: 'sticky',
  top: 0,
  width: 'fit-content',
  overflow: 'hidden',
});

const minimalTabsSx: SxProps<Theme> = ({ backrs }) => ({
  gap: backrs.spacing['spacing-md'],
  textTransform: 'none',
  flex: 1,
  '& .MuiTabs-indicator': {
    backgroundColor: backrs.colors['Colors/Foreground/fg-brand-secondary'],
  },
});

const minimalHighlightedTabsSx: SxProps<Theme> = ({ backrs }) => ({
  gap: backrs.spacing['spacing-md'],
  textTransform: 'none',
  flex: 1,
  minHeight: 'unset',
  '& .MuiTabs-indicator': {
    backgroundColor: backrs.colors['Colors/Foreground/fg-brand-secondary'],
  },
  '& .MuiTabs-flexContainer': {
    minHeight: 'unset',
  },
});

const boxTabsSx: SxProps<Theme> = ({ backrs }) => ({
  '& .MuiTabs-indicator': {
    display: 'none',
  },
  '& .MuiTab-root': {
    padding: backrs.spacing['spacing-none'],
  },
  '& .MuiButtonBase-root': {
    padding: backrs.spacing['spacing-none'],
    paddingX: {
      xs: backrs.spacing['spacing-sm'],
      lg: backrs.spacing['spacing-xl'],
    },
  },
});

const chipSx: SxProps<Theme> = ({ backrs }) => ({
  borderColor: backrs.colors['Colors/Border/border-primary'],
  '& .MuiChip-label': {
    color: backrs.colors['Colors/Text/text-secondary'],
    display: 'none',
  },
  '@container (min-width: 900px)': {
    '& .MuiChip-label': {
      display: 'block',
    },
  },
  backgroundColor: backrs.colors['Colors/Background/bg-primary'],
  alignSelf: 'center',
});

const filterContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  backgroundColor: backrs.colors['Colors/Background/bg-secondary-alt'],
  alignItems: 'flex-start',
  borderRadius: backrs.radius['radius-md'],
  border: `1px solid ${backrs.colors['Beige/950']}`,
  p: backrs.spacing['spacing-xs'],
  position: 'sticky',
  top: 0,

  transition: 'none',
  zIndex: 1,
});

const filterTabIndicatorSx: SxProps<Theme> = ({ backrs }) => ({
  height: '100%',
  top: 0,
  backgroundColor: 'transparent',
  border: `1px solid ${backrs.colors['Beige/950']}`,
  borderRadius: backrs.radius['radius-sm'],
  transition: 'left 0.3s ease, width 0.3s ease',
  zIndex: 1,
});

const filterTabSx: SxProps<Theme> = ({ backrs, typography }) => ({
  ...typography['text-sm-semibold'],
  color: backrs.colors['Colors/Text/text-quaternary'],
  backgroundColor: 'transparent',
  transition: 'color 0.5s ease',
  '&:hover': {
    backgroundColor: 'transparent',
    color: backrs.colors['Colors/Text/text-secondary'],
  },
  textTransform: 'none',
  py: backrs.spacing['spacing-md'],
  px: backrs.spacing['spacing-lg'],
  minWidth: 'unset',
  minHeight: 'unset',
  '&.Mui-selected': {
    color: backrs.colors['Colors/Text/text-secondary'],
  },
  marginRight: { sm: backrs.spacing['spacing-md'] },
  '&:last-of-type': {
    marginRight: backrs.spacing['spacing-none'],
  },
});

const filterTabsSx: SxProps<Theme> = {
  textTransform: 'none',
  flex: 1,
  '&.MuiTabs-root': {
    minWidth: 'unset',
    minHeight: 'unset',
  },
  transition: 'none',
};

const simpleContainerSx: SxProps<Theme> = (theme: Theme) => ({
  ...minimalContainerSx(theme),
  backgroundColor: theme.backrs.colors['Brand/25'],
  maxWidth: '100% !important',
  marginLeft: '0 !important',
  marginRight: '0 !important',
  // Add horizontal scrolling for smaller screens
  overflowX: 'auto',
  overflowY: 'hidden',
  scrollBehavior: 'smooth',
  // Ensure proper scrolling behavior
  '&::-webkit-scrollbar': {
    height: '4px',
  },
  '&::-webkit-scrollbar-track': {
    backgroundColor: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: theme.backrs.colors['Gray/300'],
    borderRadius: '2px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    backgroundColor: theme.backrs.colors['Gray/400'],
  },
  // Ensure proper layout for scrolling
  alignItems: 'flex-start',
  flexWrap: 'nowrap',
});

const simpleTabSx: SxProps<Theme> = (theme: Theme) => ({
  ...minimalTabSx(theme),
  backgroundColor: 'transparent',
  color: theme.backrs.colors['Colors/Text/text-quaternary'],
});

const simpleTabsSx: SxProps<Theme> = (theme: Theme) => ({
  ...minimalTabsSx(theme),
  backgroundColor: 'transparent',
  // Ensure tabs can scroll horizontally
  flexWrap: 'nowrap',
  minWidth: 'fit-content',
  '& .MuiButtonBase-root': {
    '&:first-child': {
      paddingLeft: 0,
    },
    '&:last-child': {
      paddingRight: 0,
    },
  },
});

const simpleTabIndicatorSx: SxProps<Theme> = {
  backgroundColor: 'transparent',
};

const tabTruncateSx: SxProps<Theme> = {
  minWidth: 0,
  maxWidth: '200px',
  display: 'block',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const tabWithIndicatorSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-xs'],
});

const closableTabSx: SxProps<Theme> = ({ backrs }) => ({
  pr: backrs.spacing['spacing-none'],
});

const numberedTabLabelSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-md'],
});

const numberedTabCircleSx: SxProps<Theme> = ({ backrs }) => ({
  width: ICON_SIZES['24px'],
  height: ICON_SIZES['24px'],
  borderRadius: backrs.radius['radius-full'],
  backgroundColor: backrs.colors['Brand/50'],
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: backrs.colors['Brand/700'],
  border: `1px solid ${backrs.colors['Brand/200']}`,
});

const tabCloseButtonSx: SxProps<Theme> = ({ backrs }) => ({
  padding: backrs.spacing['spacing-xs'],
  opacity: 0,
  transition: 'opacity 0.15s ease-in-out',
  '.MuiTab-root:hover &': {
    opacity: 0.6,
  },
  '&:hover': {
    opacity: 1,
    backgroundColor: backrs.colors['Gray/100'],
  },
  '@media (max-width: 900px)': {
    opacity: 0.6,
  },
});
